import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Container,
  Button,
  TextField,
  Typography,
  Tabs,
  Tab,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  Paper,
  IconButton,
  Alert,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  PhotoCamera,
  Person,
  Email,
  Phone,
  Language,
  Add,
  Close,
  Upload,
  CheckCircle,
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";
import { useMutation } from "react-query";
import { toast } from "sonner";
import Papa from "papaparse";
import client from "../../../API";
import { parseLocalFloat, parseLocalInt } from "../../../Utils/constants";
import CreatorInfo from "../../../Components/CreatorData/creatorInfo";
import PlatformDialog from "../../../Components/CreatorData/creatorPlatforms";
import SelectAssetDialog from "../../../Components/tools/selectfile";

// Platform configurations (same as in AgencyCreatorSettings)
const PLATFORM_CONFIGS = {
  Instagram: {
    color: '#E4405F',
    prefix: 'instagram.com/',
    emoji: '📸',
    isUrl: false,
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'brandRate', label: 'Brand Rate', type: 'rate', minValue: 100 },
      { key: 'songRate', label: 'Song Rate', type: 'rate', minValue: 100 },
      { key: 'feedRate', label: 'Feed Post Rate', type: 'rate', minValue: 100 },
      { key: 'storyRate', label: 'Story Rate', type: 'rate', minValue: 20 }
    ]
  },
  TikTok: {
    color: '#000000',
    prefix: 'tiktok.com/@',
    emoji: '🎵',
    isUrl: false,
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'brandRate', label: 'Brand Rate', type: 'rate', minValue: 100 },
      { key: 'songRate', label: 'Song Rate', type: 'rate', minValue: 100 }
    ]
  },
  YouTube: {
    color: '#FF0000',
    prefix: 'youtube.com/',
    emoji: '▶️',
    isUrl: false,
    fields: [
      { key: 'followerCount', label: 'Subscriber Count', type: 'number' },
      { key: 'integrationRate', label: 'Brand Integration Rate', type: 'rate', minValue: 100 },
      { key: 'shortsRate', label: 'Shorts Rate', type: 'rate', minValue: 100 },
      { key: 'dedicatedRate', label: 'Dedicated Video Rate', type: 'rate', minValue: 100 }
    ]
  },
  X: {
    color: '#000000',
    prefix: 'x.com/',
    emoji: '𝕏',
    isUrl: false,
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'feedPost', label: 'Feed Post Rate', type: 'rate', minValue: 100 },
      { key: 'repost', label: 'Repost Rate', type: 'rate', minValue: 100 }
    ]
  },
  Podcast: {
    color: '#9C27B0',
    prefix: '',
    emoji: '🎙️',
    isUrl: true,
    fields: [
      { key: 'followerCount', label: 'Listener Count', type: 'number' },
      { key: 'preroll', label: 'Preroll Ad Rate', type: 'rate', minValue: 100 },
      { key: 'midroll', label: 'Midroll Ad Rate', type: 'rate', minValue: 100 },
      { key: 'postroll', label: 'Postroll Ad Rate', type: 'rate', minValue: 100 },
      { key: 'full', label: 'Full Episode Sponsorship Rate', type: 'rate', minValue: 100 }
    ]
  },
  Kick: {
    color: '#53FC18',
    prefix: 'kick.com/',
    emoji: '🎮',
    isUrl: false,
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'placement', label: 'Product Placement Rate', type: 'rate', minValue: 100 }
    ]
  },
  Twitch: {
    color: '#9146FF',
    prefix: 'twitch.tv/',
    emoji: '🟣',
    isUrl: false,
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'placement', label: 'Product Placement Rate', type: 'rate', minValue: 100 }
    ]
  }
};

// Build API payload for specific platform
const buildPlatformPayload = (platform, data) => {
  const payload = {};
 
  switch(platform) {
    case 'Instagram':
      payload.instagram = parseLocalInt(data.followerCount) || null;
      payload.instagram_link = data.link ? `https://www.instagram.com/${data.link}` : null;
      payload.rate_ig_reelbrand = parseLocalFloat(data.brandRate);
      payload.rate_ig_reelsound = parseLocalFloat(data.songRate);
      payload.rate_ig_feedpost = parseLocalFloat(data.feedRate);
      payload.rate_ig_story = parseLocalFloat(data.storyRate);
      break;
    case 'TikTok':
      payload.tiktok = parseLocalInt(data.followerCount) || null;
      payload.tiktok_link = data.link ? `https://www.tiktok.com/@${data.link}` : null;
      payload.rate_tt_brand = parseLocalFloat(data.brandRate);
      payload.rate_tt_sound = parseLocalFloat(data.songRate);
      break;
    case 'YouTube':
      payload.youtube = parseLocalInt(data.followerCount) || null;
      payload.youtube_link = data.link ? `https://www.youtube.com/${data.link}` : null;
      payload.rate_yt_integ = parseLocalFloat(data.integrationRate);
      payload.rate_yt_short = parseLocalFloat(data.shortsRate);
      payload.rate_yt_dedicated = parseLocalFloat(data.dedicatedRate);
      break;
    case 'X':
      payload.follow_x = parseLocalInt(data.followerCount) || null;
      payload.x_link = data.link ? `https://www.x.com/${data.link}` : null;
      payload.rate_x_feedpost = parseLocalFloat(data.feedPost);
      payload.rate_x_repost = parseLocalFloat(data.repost);
      break;
    case 'Podcast':
      payload.follow_podcast = parseLocalInt(data.followerCount) || null;
      payload.podcast_link = data.link || null;
      payload.rate_podcast_preroll = parseLocalFloat(data.preroll);
      payload.rate_podcast_midroll = parseLocalFloat(data.midroll);
      payload.rate_podcast_postroll = parseLocalFloat(data.postroll);
      payload.rate_podcast_full = parseLocalFloat(data.full);
      break;
    case 'Kick':
      payload.follow_kick = parseLocalInt(data.followerCount) || null;
      payload.kick_link = data.link ? `https://kick.com/${data.link}` : null;
      payload.rate_kick_product = parseLocalFloat(data.placement);
      break;
    case 'Twitch':
      payload.twitch = parseLocalInt(data.followerCount) || null;
      payload.twitch_link = data.link ? `https://www.twitch.tv/${data.link}` : null;
      payload.rate_twitch_product = parseLocalFloat(data.placement);
      break;
  }
 
  return payload;
};

// Section Component
const Section = ({ title, icon, children }) => {
  const theme = useTheme();
  
  return (
    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
        <Box sx={{ 
          color: theme.palette.primary.main,
          display: 'flex',
          alignItems: 'center'
        }}>
          {icon}
        </Box>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Paper>
  );
};

const CreatorDialog = ({ open, onClose, onSubmit, defaultManager = "" }) => {
  const theme = useTheme();
  
  // Tab state
  const [activeTab, setActiveTab] = useState("manual");

  // Basic info state
  const [contactEmail, setContactEmail] = useState("");
  const [paymentEmail, setPaymentEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [creatorName, setCreatorName] = useState("");
  const [creatorRegion, setCreatorRegion] = useState("");
  const [primaryMarket, setPrimaryMarket] = useState([]);
  const [notesContentStyle, setNotesContentStyle] = useState("tell us about yourself");
  const [manager, setManager] = useState(defaultManager);
  const [status, setStatus] = useState("Associate");
  const [race, setRace] = useState("");
  const [gender, setGender] = useState("");
  const [location, setLocation] = useState("");
  const [pfphref, setPfphref] = useState("");

  // Platform data storage
  const [platformData, setPlatformData] = useState({});
  
  // Dialog state for platforms
  const [platformDialogOpen, setPlatformDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [dialogData, setDialogData] = useState({});
  const [dialogSaving, setDialogSaving] = useState(false);

  // Asset selection dialog
  const [openSelectAssetDialog, setOpenSelectAssetDialog] = useState(false);

  // Bulk upload state
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  // React-query mutation
  const { mutate: addNewCreator, isLoading: isSaving } = useMutation(
    client.creators.add,
    {
      onSuccess: () => {
        toast.success("Creator added successfully");
        handleClose();
        if (onSubmit) onSubmit();
      },
      onError: (error) => {
        const errorMsg = error?.response?.data?.error || error.message;
        toast.error(`Error adding creator: ${errorMsg}`);
      },
    }
  );

  // Reset form
  const resetForm = () => {
    setContactEmail("");
    setPaymentEmail("");
    setPhoneNumber("");
    setCreatorName("");
    setCreatorRegion("");
    setPrimaryMarket([]);
    setNotesContentStyle("tell us about yourself");
    setManager(defaultManager);
    setStatus("Associate");
    setRace("");
    setGender("");
    setLocation("");
    setPfphref("");
    setPlatformData({});
    setFile(null);
    setUploadStatus("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Platform dialog handlers
  const handleOpenPlatformDialog = (platform) => {
    setSelectedPlatform(platform);
    setDialogData(platformData[platform] || {});
    setPlatformDialogOpen(true);
  };

  const handleClosePlatformDialog = () => {
    setPlatformDialogOpen(false);
    setSelectedPlatform(null);
    setDialogData({});
    setDialogSaving(false);
  };

  const handleSavePlatform = async (platform, data) => {
    // Just update local state, don't make API call yet
    setPlatformData(prev => ({
      ...prev,
      [platform]: { ...data, active: true }
    }));
    
    toast.success(`${platform} configured`);
    handleClosePlatformDialog();
  };

  // Handle asset selection
  const handleAssetClick = (asset) => {
    setPfphref(asset.filename);
    setOpenSelectAssetDialog(false);
  };

  // Validate and save creator
  const handleSaveCreator = async () => {
    // Validation
    if (!creatorName) {
      toast.error("Creator name is required");
      return;
    }

    if (phoneNumber && phoneNumber.length < 11) {
      toast.error("Phone number must be at least 11 digits");
      return;
    }

    // Check if at least one platform is configured
    const activePlatforms = Object.values(platformData).filter(p => p.active);
    if (activePlatforms.length === 0) {
      toast.error("Please configure at least one platform");
      return;
    }

    try {
      // Build base payload
      const payload = {
        creator: creatorName,
        geolocation: location,
        gender,
        ethnicity: race,
        primary_markets: primaryMarket,
        region: creatorRegion,
        notescontent_style: notesContentStyle || null,
        contactEmail,
        paymentEmail,
        phone_number: phoneNumber,
        pfphref: pfphref || null,
        manager,
        status: status || "Associate",
      };

      // Add platform data to payload
      Object.entries(platformData).forEach(([platform, data]) => {
        if (data.active) {
          const platformPayload = buildPlatformPayload(platform, data);
          Object.assign(payload, platformPayload);
        }
      });

      addNewCreator(payload);
    } catch (error) {
      toast.error(`Error preparing data: ${error.message}`);
    }
  };

  // Bulk upload handlers
  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    setFile(uploadedFile);

    if (uploadedFile) {
      setIsUploading(true);
      Papa.parse(uploadedFile, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          handleBulkUpload(result.data);
        },
        error: (error) => {
          setUploadStatus(`Error parsing file: ${error.message}`);
          setIsUploading(false);
          toast.error(`Error parsing CSV: ${error.message}`);
        },
      });
    }
  };

  const handleBulkUpload = async (creators) => {
    setUploadStatus("Processing...");
    const payloads = [];
    const errors = [];

    for (const creator of creators) {
      const payload = {
        creator: creator.creatorName || "",
        geolocation: creator.location || "",
        gender: creator.gender || "",
        ethnicity: creator.race || "",
        primary_markets: creator.primaryMarket ? creator.primaryMarket.split(",") : [],
        region: creator.creatorRegion || "",
        notescontent_style: creator.notesContentStyle || null,
        tiktok: parseLocalInt(creator.tikTokFollowerCount) || null,
        tiktok_link: creator.tikTokLink ? `https://www.tiktok.com/@${creator.tikTokLink}` : null,
        instagram: parseLocalInt(creator.instagramFollowerCount) || null,
        instagram_link: creator.instagramLink ? `https://www.instagram.com/${creator.instagramLink}` : null,
        youtube: parseLocalInt(creator.youtubeFollowerCount) || null,
        youtube_link: creator.youtubeLink ? `https://www.youtube.com/${creator.youtubeLink}` : null,
        twitch: parseLocalInt(creator.twitchFollowerCount) || null,
        twitch_link: creator.twitchLink ? `https://www.twitch.tv/${creator.twitchLink}` : null,
        follow_x: parseLocalInt(creator.xFollowerCount) || null,
        x_link: creator.xLink ? `https://www.x.com/${creator.xLink}` : null,
        follow_podcast: parseLocalInt(creator.podcastFollowerCount) || null,
        podcast_link: creator.podcastLink || null,
        follow_kick: parseLocalInt(creator.kickFollowerCount) || null,
        kick_link: creator.kickLink ? `https://www.kick.com/${creator.kickLink}` : null,
        rate_tt_sound: parseLocalFloat(creator.tikTokSongRate) || null,
        rate_tt_brand: parseLocalFloat(creator.tikTokBrandRate) || null,
        rate_ig_feedpost: parseLocalFloat(creator.instagramFeedRate) || null,
        rate_ig_reelsound: parseLocalFloat(creator.instagramSongRate) || null,
        rate_ig_reelbrand: parseLocalFloat(creator.instagramBrandRate) || null,
        rate_ig_story: parseLocalFloat(creator.instagramStoryRate) || null,
        rate_yt_integ: parseLocalFloat(creator.youtubeIntegrationRate) || null,
        rate_yt_short: parseLocalFloat(creator.youtubeShortsRate) || null,
        rate_yt_dedicated: parseLocalFloat(creator.youtubeDedicatedRate) || null,
        rate_podcast_preroll: parseLocalFloat(creator.podcastPreroll) || null,
        rate_podcast_midroll: parseLocalFloat(creator.podcastMidroll) || null,
        rate_podcast_postroll: parseLocalFloat(creator.podcastPostroll) || null,
        rate_podcast_full: parseLocalFloat(creator.podcastFull) || null,
        rate_twitch_product: parseLocalFloat(creator.twitchPlacement) || null,
        rate_kick_product: parseLocalFloat(creator.kickPlacement) || null,
        rate_x_feedpost: parseLocalFloat(creator.xFeedPost) || null,
        rate_x_repost: parseLocalFloat(creator.xRepost) || null,
        contactEmail: creator.contactEmail || "",
        paymentEmail: creator.paymentEmail || "",
        phone_number: creator.phoneNumber || "",
        pfphref: creator.pfphref || null,
        manager: creator.manager || defaultManager,
        status: creator.status || "Associate",
      };

      if (creator.phoneNumber && creator.phoneNumber.length < 11) {
        errors.push(`Invalid phone number for ${creator.creatorName}`);
      } else {
        payloads.push(payload);
      }
    }

    const batchSize = 10;
    let successfulCount = 0;

    for (let i = 0; i < payloads.length; i += batchSize) {
      const batch = payloads.slice(i, i + batchSize);
      try {
        await Promise.all(
          batch.map((payload) =>
            client.creators.add(payload)
          )
        );
        successfulCount += batch.length;
        setUploadStatus(`Processed ${successfulCount} of ${payloads.length} creators...`);
      } catch (error) {
        errors.push(`Error in batch ${i / batchSize + 1}: ${error.message}`);
      }
    }

    setIsUploading(false);

    if (errors.length > 0) {
      setUploadStatus(
        `Completed with ${errors.length} errors: ${successfulCount} creators added.`
      );
      toast.warning(`Added ${successfulCount} creators with some errors`);
    } else {
      setUploadStatus(`Successfully added ${successfulCount} creators`);
      toast.success(`Successfully added ${successfulCount} creators`);
      setTimeout(() => {
        handleClose();
      }, 1500);
    }
  };

  const platformKeys = Object.keys(PLATFORM_CONFIGS);
  const activePlatformCount = Object.values(platformData).filter(p => p.active).length;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Add New Creator
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Add creators manually or upload via CSV
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Container maxWidth="lg" disableGutters>
            {/* Tab Navigation */}
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab 
                label="Manual Entry" 
                value="manual"
                icon={<Person />}
                iconPosition="start"
              />
              <Tab 
                label="Bulk Upload" 
                value="bulk"
                icon={<Upload />}
                iconPosition="start"
              />
            </Tabs>

            {/* MANUAL Tab */}
            {activeTab === "manual" && (
              <Box>
                {/* Profile Photo */}
                <Section title="Profile Photo" icon={<PhotoCamera />}>
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Avatar
                      src={pfphref}
                      sx={{ 
                        width: 100, 
                        height: 100, 
                        cursor: 'pointer',
                        boxShadow: 2,
                        fontSize: '2rem'
                      }}
                      onClick={() => setOpenSelectAssetDialog(true)}
                    >
                      {!pfphref && (creatorName ? creatorName.charAt(0).toUpperCase() : <PhotoCamera />)}
                    </Avatar>
                    <Box>
                      <Button
                        variant="outlined"
                        startIcon={<PhotoCamera />}
                        onClick={() => setOpenSelectAssetDialog(true)}
                      >
                        {pfphref ? "Change Photo" : "Select Photo"}
                      </Button>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                        Recommended: Square image, at least 400x400px
                      </Typography>
                    </Box>
                  </Stack>
                </Section>

                {/* Contact Information */}
                <Section title="Contact Information" icon={<Email />}>
                  <CreatorInfo
                    creatorName={creatorName}
                    setCreatorName={setCreatorName}
                    creatorRegion={creatorRegion}
                    setCreatorRegion={setCreatorRegion}
                    race={race}
                    setRace={setRace}
                    gender={gender}
                    setGender={setGender}
                    location={location}
                    setLocation={setLocation}
                    primaryMarket={primaryMarket}
                    setPrimaryMarket={setPrimaryMarket}
                    notesContentStyle={notesContentStyle}
                    setNotesContentStyle={setNotesContentStyle}
                    contactEmail={contactEmail}
                    setContactEmail={setContactEmail}
                    phoneNumber={phoneNumber}
                    setPhoneNumber={setPhoneNumber}
                    validationError=""
                    paymentEmail={paymentEmail}
                    setPaymentEmail={setPaymentEmail}
                  />
                </Section>

                {/* Platforms & Rates */}
                <Section title="Platforms & Rates" icon={<Language />}>
                  {activePlatformCount > 0 && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CheckCircle fontSize="small" />
                        <Typography variant="body2">
                          {activePlatformCount} platform{activePlatformCount !== 1 ? 's' : ''} configured
                        </Typography>
                      </Stack>
                    </Alert>
                  )}
                  
                  <Grid container spacing={2}>
                    {platformKeys.map((platform) => {
                      const config = PLATFORM_CONFIGS[platform];
                      const data = platformData[platform] || {};
                      const isActive = data.active;

                      return (
                        <Grid item xs={12} sm={6} md={4} key={platform}>
                          <Card
                            sx={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              borderRadius: 2,
                              border: isActive ? `2px solid ${config.color}` : '2px solid #e0e0e0',
                              boxShadow: isActive ? 3 : 1,
                              transition: 'all 0.2s',
                              '&:hover': {
                                boxShadow: 4,
                                transform: 'translateY(-2px)'
                              }
                            }}
                          >
                            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                              <Stack spacing={1.5}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                  <Avatar sx={{
                                    bgcolor: config.color,
                                    width: 40,
                                    height: 40,
                                    fontSize: '1.25rem'
                                  }}>
                                    {config.emoji}
                                  </Avatar>
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                      {platform}
                                    </Typography>
                                    {isActive && (
                                      <Chip
                                        label="Configured"
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: '0.7rem',
                                          bgcolor: alpha(config.color, 0.15),
                                          color: config.color,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Stack>

                                {isActive ? (
                                  <>
                                    {data.followerCount && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">
                                          Followers
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                          {parseInt(data.followerCount).toLocaleString()}
                                        </Typography>
                                      </Box>
                                    )}
                                    {data.link && (
                                      <Typography 
                                        variant="caption" 
                                        color="text.secondary"
                                        sx={{
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {config.prefix}{data.link}
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    Not configured
                                  </Typography>
                                )}
                              </Stack>
                            </CardContent>

                            <CardActions sx={{ pt: 0, pb: 1.5, px: 2 }}>
                              <Button
                                fullWidth
                                size="small"
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => handleOpenPlatformDialog(platform)}
                                sx={{
                                  bgcolor: config.color,
                                  '&:hover': {
                                    bgcolor: alpha(config.color, 0.8),
                                  }
                                }}
                              >
                                {isActive ? 'Edit' : 'Add'}
                              </Button>
                            </CardActions>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Section>

                {/* Manager & Status 
                <Section title="Additional Details" icon={<Person />}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Manager"
                        fullWidth
                        value={manager}
                        onChange={(e) => setManager(e.target.value)}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                          label="Status"
                        >
                          <MenuItem value="Partner">Partner</MenuItem>
                          <MenuItem value="Associate">Associate</MenuItem>
                          <MenuItem value="Agency">Agency</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Section>

                */}
                {/* Submit Button */}
                <Paper sx={{ p: 3, mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    disabled={isSaving}
                    onClick={handleSaveCreator}
                    startIcon={isSaving ? <CircularProgress size={20} /> : <CheckCircle />}
                  >
                    {isSaving ? 'Adding Creator...' : 'Add Creator'}
                  </Button>
                </Paper>
              </Box>
            )}

            {/* BULK Tab */}
            {activeTab === "bulk" && (
              <Box>
                <Section title="Bulk Upload" icon={<Upload />}>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2" gutterBottom>
                      Upload multiple creators at once using a CSV file.
                    </Typography>
                    <Typography variant="body2">
                      Make sure the "manager" column includes "{defaultManager}" so creators are assigned correctly.
                    </Typography>
                  </Alert>

                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      component="a"
                      href="https://docs.google.com/spreadsheets/d/1ofQC_Ar3Hl3JCWy4Q3hkPMfdnEumfq98RGJ5GhDIUvY/edit?gid=0#gid=0"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ mb: 2 }}
                    >
                      View CSV Template
                    </Button>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ 
                    border: 2, 
                    borderColor: 'divider', 
                    borderRadius: 2, 
                    p: 4, 
                    textAlign: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                  }}>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      id="csv-upload"
                      disabled={isUploading}
                    />
                    <label htmlFor="csv-upload">
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={isUploading ? <CircularProgress size={20} /> : <Upload />}
                        disabled={isUploading}
                        size="large"
                      >
                        {isUploading ? 'Processing...' : 'Select CSV File'}
                      </Button>
                    </label>
                    
                    {file && (
                      <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                        Selected: {file.name}
                      </Typography>
                    )}
                  </Box>

                  {uploadStatus && (
                    <Alert 
                      severity={uploadStatus.includes('Error') || uploadStatus.includes('errors') ? "error" : "success"} 
                      sx={{ mt: 2 }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {uploadStatus}
                      </Typography>
                    </Alert>
                  )}
                </Section>
              </Box>
            )}
          </Container>
        </DialogContent>
      </Dialog>

      {/* Asset Selection Dialog */}
      <SelectAssetDialog
        open={openSelectAssetDialog}
        onClose={() => setOpenSelectAssetDialog(false)}
        selectedAsset={pfphref ? { filename: pfphref } : null}
        onAssetClick={handleAssetClick}
      />

      {/* Platform Dialog */}
      {selectedPlatform && platformDialogOpen && (
        <PlatformDialog
          open={platformDialogOpen}
          platform={selectedPlatform}
          config={PLATFORM_CONFIGS[selectedPlatform]}
          data={dialogData}
          saving={dialogSaving}
          onClose={handleClosePlatformDialog}
          onSave={handleSavePlatform}
        />
      )}
    </>
  );
};

export default CreatorDialog;